
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Minus } from 'lucide-react'
import {Link} from 'react-router-dom'
const faqs = [
  {
    question: "What is aiDAPTIV+?",
    answer: "MiPhi provides access to Phison’s aiDAPTIV+, the ultimate turnkey solution for organizations to train large data models without additional staff and infrastructure. The platform scales linearly with your data training and time requirements, allowing you to focus on results."
  },
  {
    question: "How can I contact technical support?",
    answer: "You can reach our technical support team via email at support@hyco.ai"
  },
  {
    question: "What kind of support do you provide?",
    answer: "We provide comprehensive 24/7 technical support, including remote diagnostics, on-site service options, and dedicated account managers for enterprise clients. Our expert team is always available to help optimize your system performance."
  },
  {
    question: "How does the aiDAPTIV+ cache system work?",
    answer: "aiDAPTIV+ is our proprietary cache system that optimizes data flow between storage, memory, and GPUs. It automatically adjusts based on workload patterns, significantly improving AI model training and inference speeds."
  },
  {
    question: "What's the typical delivery timeline?",
    answer: "Standard delivery typically takes 4-6 weeks from order confirmation. For urgent requirements, we offer expedited delivery options. Custom configurations might require additional time based on component availability."
  },
 
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
}

const FAQs = () => {
  const [activeIndex, setActiveIndex] = useState(null)

  return (
    <div className="pt-32 min-h-screen bg-black py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Find answers to common questions about MiPhi-ANT PCs and our services. 
            Can't find what you're looking for? Contact our support team.
          </p>
        </motion.div>

        {/* FAQs Container */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative group"
            >
              {/* Glassmorphism background */}
              <div className="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-700/30 transition-colors duration-300 group-hover:bg-white/10" />
              
              {/* Content */}
              <div className="relative">
                <button
                  onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="text-lg font-medium text-white pr-8">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: activeIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="shrink-0"
                  >
                    {activeIndex === index ? (
                      <Minus className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Plus className="w-5 h-5 text-emerald-400" />
                    )}
                  </motion.div>
                </button>

                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <div className="h-px bg-gradient-to-r from-gray-500/20 via-gray-500/50 to-gray-500/20 mb-4" />
                        <p className="text-gray-300 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Hover effect gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-500/0 via-gray-500/5 to-gray-500/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-400 mb-4">
            Still have questions?
          </p>
          <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-emerald-600 text-white rounded-xl 
                       transition-colors duration-300
                      shadow-lg "
          >
            Contact Support
          </motion.button></Link>
        </motion.div>
      </div>
    </div>
  )
}

export default FAQs

